import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { HttpApiService } from '@core/auth/http-api.service';
import { UserService } from '@core/auth/user/user.service';
import { config } from '@core/services/request-url.service';
import { WalletApiResponse, WalletBalance } from './wallet.model';
import { StorageService } from '@core/storage-module/storage.service';

@Injectable({ providedIn: 'root' })
export class WalletStore {

  /* ------------------ RAW STATE ------------------ */

  private readonly _wallets = signal<WalletBalance[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  /* ------------------ PUBLIC SIGNALS ------------------ */

  readonly wallets = this._wallets.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  /* ------------------ DERIVED STATE ------------------ */

  readonly allWallet = computed(() => {
    return this._wallets();
  });

  readonly mainWallet = computed(() =>
    this._wallets().find(w => w.class === 'main_wallet')
  );

  readonly aepsWallet = computed(() =>
    this._wallets().find(w => w.class === 'aeps_wallet')
  );

  readonly qrWallet = computed(() =>
    this._wallets().find(w => w.class === 'qr_wallet')
  );

  readonly totalBalance = computed(() =>
    this._wallets()
      .reduce((sum, w) => sum + Number(w.balance || 0), 0)
      .toFixed(2)
  );

  /* ------------------ INIT ------------------ */

  constructor(
    private http: HttpApiService,
    private userService: UserService,
    private readonly storage: StorageService
  ) {
    this.restoreFromStorage();

    // Auto-persist whenever wallet changes
    effect(() => {
      this.storage.setItem(
        '_userWalletAmount',
        JSON.stringify(this._wallets())
      );
    });
  }

  /* ------------------ ACTIONS ------------------ */

  async fetchWallet(refresh = false): Promise<void> {
    if (this._wallets().length && !refresh) return;

    this._loading.set(true);
    this._error.set(null);

    try {
      const payload = {
        type: 'wallet',
        agentbalance: this.userService.getPartialData('data.agentbalance')
      };

      const resp = await firstValueFrom(
        this.http.post<WalletApiResponse>(
          config.dashboard.amount.url,
          payload
        )
      );

      if (resp?.statuscode === 200) {
        // resp.balance_enquiry.map((ele: any) => {
        //   ele['bg'] = '';
        // });
        this._wallets.set(resp.balance_enquiry);
      } else {
        this._error.set(resp?.message ?? 'Wallet fetch failed');
      }
    } catch (err: any) {
      this._error.set(err?.message ?? 'Network error');
    } finally {
      this._loading.set(false);
    }
  }

  reset(): void {
    this._wallets.set([]);
    this._error.set(null);
    this.storage.removeItem('_userWalletAmount');
  }

  /* ------------------ STORAGE ------------------ */

  private restoreFromStorage(): void {
    const stored = this.storage.getItem('_userWalletAmount');
    
    if (!stored) return;

    try {
      this._wallets.set(JSON.parse(stored));
    } catch {
      this.storage.removeItem('_userWalletAmount');
    }
  }
}
